import type { BuildingRules } from './building.js';
import type { SpawnDef } from './level.js';
import { ARCHETYPES } from './level.js';
import { Rng } from './rng.js';

/**
 * Traffic patterns.
 *
 * The v0.4 thesis, in the plan update's words: patterns should become "one of
 * the primary methods for creating variety without constantly inventing entirely
 * new gameplay systems". A level stops being a hand-written list of people and
 * becomes a *shape* of demand plus a few numbers.
 *
 * These expand to a concrete spawn list when content is loaded, deterministically
 * from the level's own seed — not at runtime. That matters: it keeps the
 * simulation, the replay format and every tool exactly as they were, and it keeps
 * a level being the same level every time you play it. The pattern is an
 * authoring convenience, never a source of live randomness.
 */

export type TrafficPattern =
  /** Everyone leaving the lobby for the floors above. */
  | 'morningRush'
  /** The building emptying downward. */
  | 'eveningRush'
  /** Two-way traffic concentrated on one floor and back again. */
  | 'lunchRush'
  /** Arrivals from the lobby, travelling heavy, sometimes together. */
  | 'checkIn'
  /** Departures toward the lobby, travelling heavy, sometimes together. */
  | 'checkOut'
  /** Everyone converging on a single floor at once. */
  | 'conference'
  /** Two waves crossing at a boundary floor: one shift up, the other down. */
  | 'shiftChange'
  /** A large downward burst with almost no warning. */
  | 'closingTime'
  /** No shape at all. The control against which the others are judged. */
  | 'scatter';

export interface ArchetypeShare {
  readonly archetype: string;
  /** How many of the generated passengers take this archetype. */
  readonly count: number;
}

/**
 * An emergency call-out (ADR-0029): a crew arriving at `atMs` at `from` — the
 * first way in, unless the level says — for an incident on `to` — a floor the
 * seed picks, unless the level says. Their return is written after them.
 */
export interface CallOutDef {
  readonly archetype: string;
  readonly atMs: number;
  readonly from?: number;
  readonly to?: number;
}

/** A building's own character in a generated level (ADR-0031): one arrival, at a set time. */
export interface CastDef {
  readonly archetype: string;
  readonly atMs: number;
  /** Where they come from. Defaults to the building's first door. */
  readonly from?: number;
  /** Where they go. Defaults to a floor drawn from the seed, never a door. */
  readonly to?: number;
  /** Floors to stop at on the way: a postman's round. */
  readonly stops?: readonly number[];
}

/**
 * A departure at a set time, and the people running for it: a train and its
 * commuters (ADR-0031), or the hotel's airport shuttle, its breakfast closing,
 * and a wedding car (ADR-0051). The departure is everyone's deadline.
 */
export interface TrainDef {
  readonly departsAtMs: number;
  /** How many are running for it, from floors drawn from the seed. */
  readonly riders: number;
  /** Where it leaves from. Defaults to the building's first door: the Transit Centre's metro. */
  readonly to?: number;
  /** Who runs for it. Defaults to commuters. */
  readonly archetype?: string;
  /** In parties of this size, each from one floor, who ride together: a wedding couple. */
  readonly party?: number;
}

export interface TrafficDef {
  readonly pattern: TrafficPattern;
  readonly count: number;
  /** Spread arrivals across this window. Zero means everyone is already there. */
  readonly overMs?: number;
  /** The floor that counts as the ground. Defaults to 0. */
  readonly lobby?: number;
  /** The floor traffic converges on, for `lunchRush` and `conference`. */
  readonly hub?: number;
  /** Where the two waves meet, for `shiftChange`. Defaults to the middle. */
  readonly boundary?: number;
  /** Archetypes sprinkled through the generated crowd. The rest are ordinary. */
  readonly mix?: readonly ArchetypeShare[];
  /** Turn some arrivals into parties who travel together, of this size. */
  readonly groupsOf?: number;
  /** How many such parties. Ignored without `groupsOf`. */
  readonly groups?: number;
  /** Emergencies, each with its return (ADR-0029). Written after the crowd. */
  readonly callouts?: readonly CallOutDef[];
  /** The building's own characters (ADR-0031). Written after the call-outs. */
  readonly cast?: readonly CastDef[];
  /** Trains to catch, and who is running for them (ADR-0031). Written after the cast. */
  readonly trains?: readonly TrainDef[];
  /**
   * No two arrivals further apart than this, from the start of the shift
   * (ADR-0038): a quiet stretch gets ordinary people in it, evenly spaced, so
   * the building is never empty with nothing to do but wait. Written last.
   */
  readonly maxGapMs?: number;
}

/**
 * What separates one pattern from another.
 *
 * Direction alone is not enough: a morning rush and a hotel check-in are both
 * "up from the lobby", and generating them identically would mean shipping two
 * names for one pattern. What actually differs is the *rhythm* people arrive in
 * and how widely they spread out once they do.
 */
interface Profile {
  /** How arrivals fall across the window. */
  readonly arrival: 'spread' | 'clustered' | 'burst';
  /** Whether demand fans out across the building or concentrates on a few floors. */
  readonly spread: 'wide' | 'narrow';
}

const PROFILES: Readonly<Record<TrafficPattern, Profile>> = {
  morningRush: { arrival: 'spread', spread: 'wide' },
  eveningRush: { arrival: 'spread', spread: 'wide' },
  lunchRush: { arrival: 'spread', spread: 'wide' },
  // Guests arrive in parties, and their rooms are near each other.
  checkIn: { arrival: 'clustered', spread: 'narrow' },
  checkOut: { arrival: 'clustered', spread: 'narrow' },
  // A session lets out: everyone at once, from everywhere.
  conference: { arrival: 'burst', spread: 'wide' },
  shiftChange: { arrival: 'clustered', spread: 'wide' },
  // The doors close and the building empties in one go.
  closingTime: { arrival: 'burst', spread: 'wide' },
  scatter: { arrival: 'spread', spread: 'wide' },
};

interface Draft {
  atMs: number;
  origin: number;
  destination: number;
  archetype?: string;
  group?: string;
}

/** Arrival times across the window, in order, so a level reads as a wave. */
function arrivals(count: number, overMs: number, profile: Profile, rng: Rng): number[] {
  if (overMs <= 0 || count === 0) return new Array(count).fill(0);
  const round = (ms: number) => Math.max(0, Math.round(ms / 100) * 100);

  if (profile.arrival === 'burst') {
    // Almost everyone at once, in the first fifth of the window: the point of a
    // burst is that there is no time to get ahead of it.
    return Array.from({ length: count }, () => round(rng.float() * overMs * 0.2)).sort(
      (a, b) => a - b,
    );
  }

  if (profile.arrival === 'clustered') {
    // Two or three knots of people rather than a steady queue.
    const knots = Math.max(2, Math.min(3, Math.ceil(count / 3)));
    const centres = Array.from({ length: knots }, (_, k) => ((k + 0.5) / knots) * overMs);
    return Array.from({ length: count }, (_, i) => {
      const centre = centres[i % knots] ?? 0;
      return round(centre + (rng.float() - 0.5) * (overMs / knots) * 0.45);
    }).sort((a, b) => a - b);
  }

  const times: number[] = [];
  for (let i = 0; i < count; i += 1) {
    // Even spacing with a little jitter: a perfectly regular queue reads as
    // machinery, and a uniformly random one has no shape at all.
    const slot = (i / Math.max(1, count - 1)) * overMs;
    const jitter = (rng.float() - 0.5) * (overMs / Math.max(2, count)) * 0.8;
    times.push(round(slot + jitter));
  }
  return times.sort((a, b) => a - b);
}

/** A floor that is not `avoid`, drawn from the whole building. */
function otherFloor(floors: number, avoid: number, rng: Rng): number {
  if (floors <= 1) return 0;
  let floor = rng.int(0, floors - 2);
  if (floor >= avoid) floor += 1;
  return floor;
}

function shape(
  def: TrafficDef,
  floors: number,
  profile: Profile,
  rng: Rng,
  building: BuildingRules,
): { origin: number; destination: number }[] {
  /**
   * Where outside traffic enters.
   *
   * v0.4 measured that concentrating origins is what flattens a level — the
   * lift's only decision is which call to answer, and a call is an origin. A
   * building with two street doors therefore un-flattens every lobby pattern it
   * touches without changing the pattern at all, which is the clearest case
   * there is for a building being structure rather than scenery.
   */
  const entrances =
    building.entrances && building.entrances.length > 0 ? building.entrances : [def.lobby ?? 0];
  // One door draws nothing. Consuming a random number to "choose" between a
  // single option shifts the whole RNG stream, which silently rebuilds every
  // generated level in the game — the replay fixtures caught exactly that.
  const entrance = () =>
    (entrances.length === 1 ? entrances[0] : entrances[rng.int(0, entrances.length - 1)]) ?? 0;
  const lobby = entrances[0] ?? 0;
  const top = floors - 1;
  const hub = Math.min(top, Math.max(0, def.hub ?? Math.round(top / 2)));
  const boundary = Math.min(top - 1, Math.max(1, def.boundary ?? Math.round(top / 2)));

  // A narrow pattern picks a small block of floors and keeps returning to it —
  // a hotel's guests are in adjacent rooms, not scattered through the tower.
  const lowUpper = Math.min(lobby + 1, top);
  const block = (() => {
    if (profile.spread === 'wide') return null;
    const width = Math.max(2, Math.round((top - lowUpper + 1) / 3));
    const start = rng.int(lowUpper, Math.max(lowUpper, top - width + 1));
    return { start, end: Math.min(top, start + width - 1) };
  })();
  const upper = () => {
    const floor = block ? rng.int(block.start, block.end) : rng.int(lowUpper, top);
    // Never hand back a door as an upstairs destination: with two entrances the
    // second one sits above the ground floor, and a lobby-to-lobby "journey" is
    // dropped later as a non-trip, quietly shrinking the crowd.
    return entrances.includes(floor) && top > lowUpper ? Math.min(top, floor + 1) : floor;
  };
  const out: { origin: number; destination: number }[] = [];

  for (let i = 0; i < def.count; i += 1) {
    switch (def.pattern) {
      case 'morningRush':
      case 'checkIn':
        out.push({ origin: entrance(), destination: upper() });
        break;
      case 'eveningRush':
      case 'checkOut':
      case 'closingTime':
        out.push({ origin: upper(), destination: entrance() });
        break;
      case 'conference':
        out.push({ origin: otherFloor(floors, hub, rng), destination: hub });
        break;
      case 'lunchRush': {
        // Half go to the hub, half come back from it.
        const toHub = i % 2 === 0;
        const other = otherFloor(floors, hub, rng);
        out.push(toHub ? { origin: other, destination: hub } : { origin: hub, destination: other });
        break;
      }
      case 'shiftChange': {
        // One shift going home, the next arriving — crossing at the boundary.
        const goingUp = i % 2 === 0;
        out.push(
          goingUp
            ? { origin: rng.int(0, boundary - 1), destination: rng.int(boundary, top) }
            : { origin: rng.int(boundary, top), destination: rng.int(0, boundary - 1) },
        );
        break;
      }
      case 'scatter': {
        const origin = rng.int(0, top);
        out.push({ origin, destination: otherFloor(floors, origin, rng) });
        break;
      }
    }
  }

  return out;
}

/**
 * Expand a traffic definition into the spawn list a level actually runs.
 *
 * Given the same definition and seed this returns the same people, in the same
 * order, forever — which is what lets a generated level be a *designed* level
 * rather than a lottery.
 */
export function generateTraffic(
  def: TrafficDef,
  floors: number,
  seed: number,
  building: BuildingRules = {},
): SpawnDef[] {
  if (def.count <= 0) return [];
  const rng = new Rng(seed);

  const profile = PROFILES[def.pattern];
  const routes = shape(def, floors, profile, rng, building);
  const times = arrivals(def.count, def.overMs ?? 0, profile, rng);

  const drafts: Draft[] = routes.map((route, i) => ({
    atMs: times[i] ?? 0,
    origin: route.origin,
    destination: route.destination,
  }));

  // Parties travel together, so they have to share a route as well as a tag.
  const partySize = def.groupsOf ?? 0;
  const parties = partySize > 1 ? (def.groups ?? 1) : 0;
  let cursor = 0;
  for (let p = 0; p < parties; p += 1) {
    const lead = drafts[cursor];
    if (!lead || cursor + partySize > drafts.length) break;
    for (let m = 0; m < partySize; m += 1) {
      const member = drafts[cursor + m];
      if (!member) break;
      member.origin = lead.origin;
      member.destination = lead.destination;
      member.atMs = lead.atMs;
      member.group = `party-${p + 1}`;
    }
    cursor += partySize;
  }

  // Archetypes go to the people who are NOT already in a party, so a trait and a
  // group never silently land on the same passenger and confuse both.
  const free = drafts.map((_, i) => i).filter((i) => drafts[i]?.group === undefined);
  let next = 0;
  for (const share of def.mix ?? []) {
    for (let n = 0; n < share.count && next < free.length; n += 1) {
      const index = free[next] as number;
      const draft = drafts[index];
      if (draft) draft.archetype = share.archetype;
      next += 1;
    }
  }

  const crowd: SpawnDef[] = drafts
    .filter((d) => d.origin !== d.destination)
    .map((d) => ({
      atMs: d.atMs,
      origin: d.origin,
      destination: d.destination,
      ...(d.archetype === undefined ? {} : { archetype: d.archetype }),
      ...(d.group === undefined ? {} : { group: d.group }),
    }));
  // After everyone else, so a level's crowd is the same with or without them —
  // and they draw from the seed only when there are some.
  const crews = callOuts(def, floors, building, rng, crowd.length);
  const cast = castOf(def, floors, building, rng, crowd.length + crews.length);
  const spawns = [...crowd, ...crews, ...cast, ...trainsOf(def, floors, building, rng)];
  return [...spawns, ...fillGaps(spawns, def.maxGapMs, floors, doorsOf(building), rng)];
}

/**
 * Ordinary people for every stretch longer than `maxGapMs` with nobody
 * arriving, the start of the shift included (ADR-0038). They come in at a
 * door, as someone off the street does, so a full landing never turns them
 * away. Only those who arrive at a set time count: a call-out's return comes
 * when its crew is done. Nothing is drawn from the seed without a `maxGapMs`,
 * so every other level's traffic is exactly what it was.
 */
function fillGaps(
  spawns: readonly SpawnDef[],
  maxGapMs: number | undefined,
  floors: number,
  doors: readonly number[],
  rng: Rng,
): SpawnDef[] {
  if (maxGapMs === undefined || maxGapMs <= 0) return [];
  const times = spawns
    .filter((spawn) => spawn.follows === undefined)
    .map((spawn) => spawn.atMs)
    .sort((a, b) => a - b);
  const out: SpawnDef[] = [];
  let last = 0;
  for (const next of times) {
    const gap = next - last;
    const extra = Math.ceil(gap / maxGapMs) - 1;
    for (let k = 1; k <= extra; k += 1) {
      const origin = doors[rng.int(0, doors.length - 1)] ?? 0;
      out.push({
        atMs: Math.round((last + (gap * k) / (extra + 1)) / 100) * 100,
        origin,
        destination: otherFloor(floors, origin, rng),
      });
    }
    last = Math.max(last, next);
  }
  return out;
}

function doorsOf(building: BuildingRules): readonly number[] {
  return building.entrances && building.entrances.length > 0 ? building.entrances : [0];
}

/**
 * One arrival from a door to a floor that is not one — the floor drawn from the
 * seed unless given — and, for a call-out, its return after it.
 */
function visit(
  entry: CallOutDef | CastDef,
  floors: number,
  building: BuildingRules,
  rng: Rng,
  index: number,
): SpawnDef[] {
  const who = ARCHETYPES[entry.archetype];
  if (!who) throw new Error(`Unknown archetype "${entry.archetype}"`);
  const doors = doorsOf(building);
  const from = entry.from ?? (doors[0] as number);
  let to = entry.to ?? rng.int(0, floors - 1);
  while (entry.to === undefined && (to === from || doors.includes(to))) {
    to = (to + 1) % floors;
  }
  const stops = 'stops' in entry && entry.stops ? { stops: entry.stops } : {};
  const out: SpawnDef[] = [
    { atMs: entry.atMs, origin: from, destination: to, archetype: entry.archetype, ...stops },
  ];
  if (who.callout) {
    out.push({
      atMs: who.callout.stayMs,
      origin: to,
      destination: from,
      archetype: who.callout.back,
      follows: index,
    });
  }
  return out;
}

/** Each building character, in the order written (ADR-0031). */
function castOf(
  def: TrafficDef,
  floors: number,
  building: BuildingRules,
  rng: Rng,
  first: number,
): SpawnDef[] {
  const out: SpawnDef[] = [];
  for (const entry of def.cast ?? []) {
    if (ARCHETYPES[entry.archetype]?.critical) {
      throw new Error(`"${entry.archetype}" is an emergency: it belongs in \`callouts\``);
    }
    out.push(...visit(entry, floors, building, rng, first + out.length));
  }
  return out;
}

/**
 * Commuters for each train (ADR-0031): they turn up on floors drawn from the
 * seed between 45 and 20 seconds before it leaves, all bound for the platform,
 * and the departure is their deadline.
 */
function trainsOf(def: TrafficDef, floors: number, building: BuildingRules, rng: Rng): SpawnDef[] {
  const out: SpawnDef[] = [];
  (def.trains ?? []).forEach((train, t) => {
    const to = train.to ?? (doorsOf(building)[0] as number);
    const archetype = train.archetype ?? 'commuter';
    const earliest = Math.max(0, train.departsAtMs - 45_000);
    const latest = Math.max(earliest, train.departsAtMs - 20_000);
    // A party draws one time and one floor for all of it; alone, each draws their own.
    const party = Math.max(1, train.party ?? 1);
    const parties = Math.ceil(train.riders / party);
    const times = Array.from({ length: parties }, () => rng.int(earliest, latest));
    times
      .sort((a, b) => a - b)
      .forEach((atMs, p) => {
        let origin = rng.int(0, floors - 1);
        while (origin === to) origin = (origin + 1) % floors;
        const members = Math.min(party, train.riders - p * party);
        for (let m = 0; m < members; m += 1) {
          out.push({
            atMs,
            origin,
            destination: to,
            archetype,
            traits: { deadlineMs: train.departsAtMs },
            ...(party > 1 ? { group: `departure-${t + 1}-${p + 1}` } : {}),
          });
        }
      });
  });
  return out;
}

/** Each call-out as two spawns: the crew up, and their return after it (ADR-0029). */
function callOuts(
  def: TrafficDef,
  floors: number,
  building: BuildingRules,
  rng: Rng,
  first: number,
): SpawnDef[] {
  const out: SpawnDef[] = [];
  for (const call of def.callouts ?? []) {
    if (!ARCHETYPES[call.archetype]?.callout) {
      throw new Error(`"${call.archetype}" is not a crew that can be called out`);
    }
    // An incident upstairs, never at a door: somewhere the crew has to be taken.
    out.push(...visit(call, floors, building, rng, first + out.length));
  }
  return out;
}

export const TRAFFIC_PATTERNS: readonly TrafficPattern[] = [
  'morningRush',
  'eveningRush',
  'lunchRush',
  'checkIn',
  'checkOut',
  'conference',
  'shiftChange',
  'closingTime',
  'scatter',
];

/** One line each, for level authoring and for the tools that report on them. */
export const TRAFFIC_DESCRIPTIONS: Readonly<Record<TrafficPattern, string>> = {
  morningRush: 'The lobby empties upward.',
  eveningRush: 'The building empties downward.',
  lunchRush: 'Two-way traffic concentrated on one floor.',
  checkIn: 'Arrivals from the lobby, travelling heavy.',
  checkOut: 'Departures toward the lobby, travelling heavy.',
  conference: 'Everyone converging on a single floor.',
  shiftChange: 'Two waves crossing at a boundary floor.',
  closingTime: 'A large downward burst with no warning.',
  scatter: 'No shape at all — the control the others are judged against.',
};
